import Phaser from 'phaser';
import { nameText } from '../../content/names';
import type { BiomeId } from '../../content/biomes';
import { builderOpen, customLevel, type CustomLawn } from '../../meta/customLawns';
import { communityBoard } from '../../meta/online/boards';
import { communityLawn } from '../../meta/online/communityLawn';
import type {
  BoardResponse,
  CommunityLawn,
  CommunityList,
  GhostResponse,
} from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { formatTime } from '../ui/widgets';
import {
  blockPlayer,
  communityLawns,
  countCommunityPlay,
  loadBoard,
  loadGhost,
  onlineOn,
  reportCommunityLawn,
  takeDownCommunityLawn,
} from '../online';
import { saves } from '../records';
import { bevelButton, clearScene, lawnThumbnail, navyPanel, thumbnailScale } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { drawWorldIcon } from '../ui/zoneArt';
import { playCustomLawn } from './customPlay';
import { wipeTo } from './WipeScene';
import { whoText } from '../nicknames';

type Sort = 'new' | 'popular';

/** A community lawn's own lawn, decoded from its code (null if this game cannot read it). */
function lawnOf(c: CommunityLawn): CustomLawn | null {
  // Its own id from its code: the same lawn on every phone and in the server's run check.
  return communityLawn(c.code);
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 'S'}`;

/**
 * CLUB > MADE, the community lawns (docs/ONLINE.md, lm-ocm.8; docs/ONLINE_UI.md): lawns players
 * built and shared, newest or most played first, and MY LAWNS to build and post your own. A
 * lawn's card shows it, who made it, how many have played it, and PLAY, REPORT (asks first; two
 * reports hide a lawn until it is reviewed) and BLOCK MAKER (asks first; their lawns stop showing
 * for you), or TAKE DOWN for your own. No likes (owner, 2026-10-28): plays, counted once per
 * player, show what is popular.
 */
export class CommunityScene extends Phaser.Scene {
  private sort: Sort = 'new';
  private list: CommunityList | null = null;
  private loading = false;
  private picked: CommunityLawn | null = null;
  private confirmReport = false;
  private confirmBlock = false;
  private note: string | null = null;
  private asked = 0;
  /** The picked lawn's board and best run (online), once loaded. */
  private best: { board: BoardResponse | null; ghost: GhostResponse | null } | null = null;

  constructor() {
    super('Community');
  }

  init(): void {
    this.list = null;
    this.picked = null;
    this.confirmReport = false;
    this.note = null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    void this.fetchPage();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private async fetchPage(more = false): Promise<void> {
    const asked = ++this.asked;
    this.loading = true;
    this.redraw();
    const page = await communityLawns(this.sort, more ? this.list?.next : null);
    if (asked !== this.asked) return;
    this.loading = false;
    if (more && this.list && page) {
      this.list = { lawns: [...this.list.lawns, ...page.lawns], next: page.next };
    } else this.list = page;
    this.redraw();
  }

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    const top = drawClubBar(this, 'made');
    const g = this.add.graphics();
    const bottom = height - TABBAR_H - 4;
    if (this.picked) {
      this.drawLawn(this.picked, width, top, bottom);
      return;
    }
    // Your own lawns: the builder (a coin unlock found in RUNS) builds and posts them.
    const mine = builderOpen(saves().save);
    const backY = mine ? bottom - 20 : bottom - 10;
    if (mine) {
      bevelButton(this, 6, backY, width - 12, 20, 'MY LAWNS: BUILD, POST', () =>
        wipeTo(this, 'MyLawns', {}),
      );
    } else {
      label(this, width / 2, backY, 'Unlock the builder in RUNS.', 1, UI.dim).setOrigin(0.5, 0);
    }
    if (!onlineOn()) {
      label(this, width / 2, top + 16, 'Online scores are off.', 1, UI.accent).setOrigin(0.5, 0);
      label(this, width / 2, top + 30, 'Turn them on in Settings,', 1, UI.dim).setOrigin(0.5, 0);
      label(this, width / 2, top + 40, 'Online, to share lawns.', 1, UI.dim).setOrigin(0.5, 0);
      return;
    }
    const half = Math.floor((width - 16) / 2);
    const sortButton = (x: number, sort: Sort, text: string) =>
      bevelButton(
        this,
        x,
        top + 4,
        half,
        16,
        text,
        () => {
          if (this.sort === sort) return;
          this.sort = sort;
          this.list = null;
          void this.fetchPage();
        },
        this.sort === sort,
      );
    sortButton(6, 'new', 'NEW');
    sortButton(10 + half, 'popular', 'POPULAR');

    const list = this.list;
    if (!list || list.lawns.length === 0) {
      const text = this.loading
        ? 'Loading...'
        : !list
          ? "Can't reach the server right now."
          : 'No lawns yet. Build one in My Lawns and POST it!';
      label(this, width / 2, top + 34, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }
    const rowH = 24;
    let y = top + 26;
    const more = list.next ? 22 : 0;
    const room = Math.floor((backY - 6 - more - y) / (rowH + 2));
    for (const c of list.lawns.slice(0, room)) {
      navyPanel(g, 4, y, width - 8, rowH);
      drawWorldIcon(g, c.biome as BiomeId, 10, y + 6);
      label(this, 24, y + 4, c.name.toUpperCase(), 1, c.mine ? UI.select : UI.accent);
      label(this, width - 10, y + 4, plural(c.plays, 'PLAY'), 1, UI.text).setOrigin(1, 0);
      label(this, 24, y + 14, whoText(c.by.player, c.by.name), 1, UI.dim);
      this.add
        .zone(4, y, width - 8, rowH)
        .setOrigin(0)
        .setData('label', c.name)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          this.picked = c;
          this.note = null;
          this.confirmReport = false;
          this.confirmBlock = false;
          this.best = null;
          this.redraw();
          void this.loadBest(c);
        });
      y += rowH + 2;
    }
    if (list.next) {
      bevelButton(this, 6, backY - 24, width - 12, 18, 'MORE', () => void this.fetchPage(true));
    }
  }

  /** A lawn's card: the lawn, who made it, its plays, and what can be done with it. */
  private drawLawn(c: CommunityLawn, width: number, top: number, bottom: number): void {
    const g = this.add.graphics();
    const backY = bottom - 20;
    bevelButton(this, 6, backY, width - 12, 20, 'BACK', () => {
      this.picked = null;
      this.redraw();
    });
    const lawn = lawnOf(c);
    label(this, width / 2, top + 4, c.name.toUpperCase(), 1, UI.accent).setOrigin(0.5, 0);
    label(this, width / 2, top + 14, `by ${whoText(c.by.player, c.by.name)}`, 1, UI.dim).setOrigin(
      0.5,
      0,
    );
    label(this, width / 2, top + 24, plural(c.plays, 'PLAY'), 1, UI.text).setOrigin(0.5, 0);
    // The lawn's best time, and this player's place on its board.
    const board = this.best?.board;
    const leader = board?.top[0];
    const bestLine = !this.best
      ? 'Loading times...'
      : leader
        ? `Best ${formatTime(leader.value, TICK_RATE)} ${whoText(leader.player, leader.name)}`
        : 'No times yet: be the first!';
    label(this, width / 2, top + 34, bestLine, 1, leader ? UI.accent : UI.dim).setOrigin(0.5, 0);
    if (board?.me) {
      label(
        this,
        width / 2,
        top + 44,
        `You: #${board.me.rank} of ${board.total}, ${formatTime(board.me.value, TICK_RATE)}`,
        1,
        UI.text,
      ).setOrigin(0.5, 0);
    }
    // The buttons sit above BACK; the lawn takes the room between.
    const buttonsTop = backY - (c.mine ? 52 : 74);
    if (lawn) {
      const level = customLevel(lawn);
      const thumbY = top + (board?.me ? 58 : 48);
      const px = thumbnailScale(level, width - 40, Math.min(150, buttonsTop - 22 - thumbY));
      const thumb = this.add
        .image(width / 2, thumbY, lawnThumbnail(this, level, px, false))
        .setOrigin(0.5, 0);
      g.lineStyle(1, 0xffffff).strokeRect(
        Math.round(thumb.x - thumb.width / 2) - 1.5,
        thumb.y - 1.5,
        thumb.width + 3,
        thumb.height + 3,
      );
    } else {
      label(this, width / 2, top + 46, 'This lawn needs a newer game.', 1, UI.dim).setOrigin(
        0.5,
        0,
      );
    }
    if (this.note) {
      label(this, width / 2, buttonsTop - 14, this.note, 1, UI.accent)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }
    if (this.confirmBlock) {
      label(this, width / 2, buttonsTop - 4, `Block ${nameText(c.by.name)}?`, 1, UI.text).setOrigin(
        0.5,
        0,
      );
      label(this, width / 2, buttonsTop + 6, 'Their lawns will be hidden.', 1, UI.dim).setOrigin(
        0.5,
        0,
      );
      bevelButton(
        this,
        6,
        buttonsTop + 20,
        width - 12,
        22,
        'BLOCK',
        () => void this.block(c),
        true,
      );
      bevelButton(this, 6, buttonsTop + 46, width - 12, 18, 'CANCEL', () => {
        this.confirmBlock = false;
        this.redraw();
      });
      return;
    }
    if (this.confirmReport) {
      label(this, width / 2, buttonsTop - 4, 'Report this lawn for a look?', 1, UI.text).setOrigin(
        0.5,
        0,
      );
      bevelButton(
        this,
        6,
        buttonsTop + 10,
        width - 12,
        22,
        'REPORT',
        () => void this.report(c),
        true,
      );
      bevelButton(this, 6, buttonsTop + 36, width - 12, 18, 'CANCEL', () => {
        this.confirmReport = false;
        this.redraw();
      });
      return;
    }
    if (lawn) {
      // RACE THE BEST beside PLAY when someone else holds the best time.
      const ghost = this.best?.ghost;
      const theirs = ghost && board?.top[0] && board.top[0].player !== board.me?.player;
      if (ghost && theirs) {
        const w = Math.floor((width - 16) / 2);
        bevelButton(this, 6, buttonsTop, w, 24, 'PLAY', () => this.play(c, lawn), true);
        bevelButton(this, 10 + w, buttonsTop, w, 24, 'RACE BEST', () => this.play(c, lawn, ghost));
      } else {
        bevelButton(this, 6, buttonsTop, width - 12, 24, 'PLAY', () => this.play(c, lawn), true);
      }
    }
    const half = Math.floor((width - 16) / 2);
    if (c.mine) {
      bevelButton(
        this,
        6,
        buttonsTop + 28,
        width - 12,
        18,
        'TAKE DOWN',
        () => void this.takeDown(c),
      );
      return;
    }
    bevelButton(this, 6, buttonsTop + 28, half, 18, 'REPORT', () => {
      this.confirmReport = true;
      this.redraw();
    });
    bevelButton(this, 10 + half, buttonsTop + 28, half, 18, 'BLOCK MAKER', () => {
      this.confirmBlock = true;
      this.redraw();
    });
  }

  /** The picked lawn's board and best run, for its card (nothing offline or with online off). */
  private async loadBest(c: CommunityLawn): Promise<void> {
    const board = communityBoard(c.id);
    const [top, ghost] = await Promise.all([loadBoard(board, 'all'), loadGhost(board, 'all')]);
    if (this.picked?.id !== c.id) return;
    this.best = { board: top, ghost };
    this.redraw();
  }

  /** Plays the lawn (racing `ghost`, its best run, if given); a clear goes on its board. */
  private play(c: CommunityLawn, lawn: CustomLawn, ghost?: GhostResponse): void {
    const back = { scene: 'Community', label: 'Lawns', data: {} };
    const race = ghost
      ? { run: ghost.run, name: ghost.name, ticks: ghost.value, kind: 'best' as const }
      : undefined;
    const options = {
      kept: false,
      back,
      community: { id: c.id, code: c.code },
      ...(race ? { ghost: race } : {}),
    };
    if (!playCustomLawn(this, lawn, options)) {
      this.note = 'None of your mowers clear it.';
      this.redraw();
      return;
    }
    void countCommunityPlay(c.id);
  }

  private async report(c: CommunityLawn): Promise<void> {
    const ok = await reportCommunityLawn(c.id);
    this.confirmReport = false;
    this.note = ok ? 'Thanks. It will get a look.' : "Can't reach the server right now.";
    this.redraw();
  }

  /** Blocks the lawn's maker: back to the list, without any of their lawns. */
  private async block(c: CommunityLawn): Promise<void> {
    const ok = await blockPlayer(c.by.player);
    this.confirmBlock = false;
    if (!ok) {
      this.note = "Can't reach the server right now.";
      this.redraw();
      return;
    }
    this.picked = null;
    this.list = this.list && {
      ...this.list,
      lawns: this.list.lawns.filter((l) => l.by.player !== c.by.player),
    };
    this.redraw();
  }

  private async takeDown(c: CommunityLawn): Promise<void> {
    if (!(await takeDownCommunityLawn(c.id))) {
      this.note = "Can't reach the server right now.";
      this.redraw();
      return;
    }
    this.picked = null;
    this.list = this.list && { ...this.list, lawns: this.list.lawns.filter((l) => l.id !== c.id) };
    this.redraw();
  }
}
